import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const goteborgKommunerText = getCityMunicipalityList("göteborg");

export const majornaPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-majorna",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling Majorna – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Majorna. Vi hämtar möbler, vitvaror och skräp i Kungsladugård, Stigberget, Sandarna, Mariaplan och Klippan. Bärhjälp i landshövdingehus, fast pris.",
      h1: "Bortforsling Majorna",
    },

    hero: {
      heading: "Bortforsling i Majorna",
      intro:
        "Boka bortforsling i Majorna – vi hämtar möbler, vitvaror och skrymmande föremål i hela stadsdelen, från lägenheterna i landshövdingehusen kring Karl Johansgatan och Kungsladugård till Stigberget, Sandarna och Klippan. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Tusentals slutförda uppdrag i Göteborgsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Majorna – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Majorna",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Majorna och körs till Högsbo återvinningscentral",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från landshövdingehus i Kungsladugård, Majorna",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling kring Karl Johansgatan, Majorna",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Stigberget, Majorna",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som bärs ner i trapphus vid bortforsling i Sandarna, Majorna",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling vid Mariaplan, Majorna",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Majorna – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Majorna är en av Göteborgs mest karaktäristiska stadsdelar – belägen väster om centrum mellan Göta älv, Slottsskogen och Älvsborgsbron. Bebyggelsen domineras av de klassiska landshövdingehusen med stenbotten och två våningar i trä, byggda för arbetarfamiljer och i regel utan hiss. Det gör bortforsling i Majorna till en speciell utmaning: tunga möbler och vitvaror ska bäras ner för smala trätrappor, ofta i hus utan hiss och med begränsad parkering på kullerstensgatorna. Med Ungdomskraft slipper du allt krångel. Vi skickar unga, starka medarbetare med skåpbil direkt till din port.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, lastar i bilen och kör till närmaste återvinningscentral. För Majorna är Högsbo återvinningscentral närmast, strax söder om stadsdelen, och Göteborgs Stad bygger dessutom en ny och större kretsloppspark i Högsbo med fokus på återbruk. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att spårvagnarna och Karl Johansgatan binder ihop stadsdelen, med Mariaplan som nav, känner vi Majornas gator, gårdar och trapphus väl. Det gör att vi ofta kan vara på plats redan samma dag du bokar. Våra medarbetare är vana vid just de trånga trätrapporna i Kungsladugård, Stigberget och Sandarna – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Majorna",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet, inklusive bärningen i hus utan hiss. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från lägenheten ut till porten. Eftersom de flesta landshövdingehus saknar hiss är det bra om du berättar vilken våning du bor på vid bokningen, så planerar vi bemanningen. Kontrollera om det finns en lastzon, innergård eller plats nära porten där vi kan ställa skåpbilen – på de smalare gatorna i Majorna kan parkering vara begränsad, så all info hjälper.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs för att få ut dem genom trånga dörrar och trappor. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som göteborgare på Högsbo återvinningscentral.",
      additionalSections: [
        {
          heading: "Områden vi täcker i Majorna",
          content:
            "Vi utför bortforsling i hela Majorna och de angränsande delarna av Carl Johans församling. Oavsett om du bor i ett landshövdingehus vid Karl Johansgatan, en lägenhet i Kungsladugård, en etta i Stigberget eller nära vattnet vid Klippan kan vi vara på plats, ofta redan samma dag.\n\nKring Mariaplan och Karl Johansgatan – stadsdelens hjärta – har vi stor vana vid hämtningar i landshövdingehus och äldre flerbostadshus, ofta utan hiss. I Kungsladugård och Sandarna möter vi både landshövdingehus och funkisbebyggelse med trapphus och gemensamma gårdar.\n\nVi är minst lika snabba i de övriga delarna: Stigberget med sina branta gränder och konsertscener, Kungssten och Sjöbergen, samt Klippan och Röda Sten nere vid Göta älv och Älvsborgsbron. Tack vare närheten till spårvagnsnätet, Oscarsleden och Slottsskogsgatan planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för något område.",
        },
        {
          heading: "Återvinning och miljöansvar i Majorna",
          content:
            "En viktig del av vår bortforsling i Majorna är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler. I Göteborg sköts avfallet av Kretslopp och vatten, och den anläggning vi använder mest för uppdrag i Majorna är:\n\nHögsbo återvinningscentral – den närmaste stationen, strax söder om Majorna, där sorterat grovavfall, elavfall, förpackningar och farligt avfall tas emot. Göteborgs Stad bygger nu Kretsloppspark Högsbo, en ny och tre gånger större anläggning med reparationsverkstad, återbruksbutik och insamling för återbruk. Eftersom Högsbo ligger nära håller vi restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Stadsmissionen och Emmaus. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen. I Majorna, med sin starka secondhand- och återbrukskultur, ligger det extra nära till hands.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Majorna med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Majornas landshövdingehus, trånga trätrappor och kullerstensgator, från Karl Johansgatan och Mariaplan till Stigberget och Klippan, och vet att Högsbo återvinningscentral ligger nära. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Vana vid hus utan hiss –",
              text: "De flesta landshövdingehus i Majorna saknar hiss. Vi är specialiserade på att bära ner tunga möbler och vitvaror för smala trätrappor – tryggt och utan att skada trapphuset. Inga extra avgifter för trappor.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för något område i Majorna, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din innergård, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via Högsbo återvinningscentral och Kretslopp och vattens anläggningar. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Majorna?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för något område i Majorna.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Majorna?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Kan ni bära ner tunga möbler i landshövdingehus utan hiss i Majorna?",
        answer:
          "Ja, det är vår specialitet. De flesta landshövdingehus i Majorna saknar hiss och har smala trätrappor. Vi är vana vid att bära ner soffor, vitvaror, sängar och annat tryggt och utan att skada trapphuset – oavsett våning, och utan extra avgift för trappor.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Majorna?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och känner Majornas gator väl, så vi tar oss snabbt dit. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Majorna?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Hur gör ni med parkering på de smala gatorna i Majorna?",
        answer:
          "Vi är vana vid Majornas trånga och ibland kullerstensbelagda gator. Berätta vid bokningen om det finns en lastzon, innergård eller plats nära porten, så planerar vi var skåpbilen ska stå för att lastningen ska gå smidigt.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom trånga dörrar eller nedför smala trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Majorna försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din innergård och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Majorna?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. För Majorna är det oftast Högsbo återvinningscentral strax söder om stadsdelen, som drivs av Kretslopp och vatten. Det som kan återanvändas skänks till Stadsmissionen eller Emmaus.",
      },
      {
        question: "Kan jag boka bortforsling i Majorna på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller vind i Majorna?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källarförråd, vindsförråd och gemensamma utrymmen som är vanliga i Majornas landshövdingehus. Vi bär upp och ner för trapporna åt dig.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Majorna?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet vid Mariaplan och ett förråd på en annan adress i Majorna.",
      },
      {
        question: "Vilka områden i Majorna täcker ni?",
        answer:
          "Vi utför bortforsling i hela Majorna – Stigberget, Kungsladugård, Sandarna, Sanna, Kungssten, Sjöbergen, Klippan och Röda Sten samt området kring Karl Johansgatan och Mariaplan. Inga extra avgifter för något område.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Majorna?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som göteborgare på Högsbo återvinningscentral.",
      },
      {
        question: "Vilka kommuner runt Majorna utför ni bortforsling i?",
        answer: `Vi finns i hela Göteborgsregionen, inklusive följande kommuner: ${goteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Majorna i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Majorna kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft och bär ner möbler och vitvaror för de smala trätrapporna i landshövdingehusen – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Majorna idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Majorna, även i hus utan hiss.",
    },

    en: {
      slug: "junk-removal-majorna",
      meta: {
        title: "Junk Removal Majorna – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Majorna. We pick up furniture, appliances and bulky items in Kungsladugård, Stigberget, Sandarna, Mariaplan and Klippan. Carrying in walk-up buildings, fixed price.",
        h1: "Junk Removal Majorna",
      },
      hero: {
        heading: "Junk Removal in Majorna",
        intro:
          "Book junk removal in Majorna – we collect furniture, appliances and bulky items across the whole district, from the apartments in the landshövdingehus buildings around Karl Johansgatan and Kungsladugård to Stigberget, Sandarna and Klippan. Quick booking, fixed price and often available the same day. Thousands of completed jobs in the Gothenburg region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Majorna – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Majorna",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Majorna and driven to Högsbo recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a landshövdingehus building in Kungsladugård, Majorna",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal around Karl Johansgatan, Majorna",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Stigberget, Majorna",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances carried down the stairwell during junk removal in Sandarna, Majorna",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal at Mariaplan, Majorna",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Majorna – fast, hassle-free and responsible",
        mainText:
          "Majorna is one of Gothenburg's most characterful districts – located west of the centre between the Göta älv river, Slottsskogen park and the Älvsborg bridge. The buildings are dominated by the classic landshövdingehus, with a stone ground floor and two wooden upper floors, built for working-class families and usually without an elevator. That makes junk removal in Majorna a special challenge: heavy furniture and appliances have to be carried down narrow wooden staircases, often in buildings without an elevator and with limited parking on the cobbled streets. With Ungdomskraft, you skip the hassle entirely. We send young, strong workers with a van directly to your entrance.\n\nOur team handles the entire chain: we carry items out of your apartment, load them into the van and drive to the nearest recycling centre. For Majorna, Högsbo recycling centre is the closest, just south of the district, and the City of Gothenburg is also building a new, larger circular-economy park in Högsbo with a focus on reuse. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the trams and Karl Johansgatan tie the district together, with Mariaplan as the hub, we know Majorna's streets, courtyards and stairwells well. This means we can often be on site the same day you book. Our workers are used to exactly those narrow wooden staircases in Kungsladugård, Stigberget and Sandarna – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Majorna",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work, including the carrying in buildings without an elevator. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the apartment out to the entrance. Since most landshövdingehus buildings have no elevator, it helps if you tell us which floor you live on when booking, so we can plan the crew. Check whether there is a loading zone, courtyard or space near the entrance where we can put the van – on the narrower streets of Majorna parking can be limited, so any information helps.\n\nGather the items in one place if possible, for example in the hallway. You don't need to disassemble furniture – we include that in our service if needed to get items through narrow doors and stairs. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Gothenburg resident you can drop those off for free at Högsbo recycling centre.",
        additionalSections: [
          {
            heading: "Areas we cover in Majorna",
            content:
              "We perform junk removal across the whole of Majorna and the adjoining parts of the Carl Johan parish. Whether you live in a landshövdingehus on Karl Johansgatan, an apartment in Kungsladugård, a studio in Stigberget or near the water at Klippan, we can be on site, often the same day.\n\nAround Mariaplan and Karl Johansgatan – the heart of the district – we are highly experienced with pickups in landshövdingehus and older apartment buildings, often without an elevator. In Kungsladugård and Sandarna we meet both landshövdingehus and functionalist buildings with stairwells and shared courtyards.\n\nWe are just as quick in the other parts: Stigberget with its steep lanes and concert venues, Kungssten and Sjöbergen, and Klippan and Röda Sten down by the Göta älv and the Älvsborg bridge. Thanks to the proximity of the tram network, Oscarsleden and Slottsskogsgatan, we always plan the route in advance so you don't have to wait. No extra charges for any area.",
          },
          {
            heading: "Recycling and environmental responsibility in Majorna",
            content:
              "An important part of our junk removal in Majorna is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres. In Gothenburg, waste is handled by Kretslopp och vatten, and the facility we use most for jobs in Majorna is:\n\nHögsbo recycling centre – the closest station, just south of Majorna, which receives sorted bulky waste, e-waste, packaging and hazardous waste. The City of Gothenburg is now building Kretsloppspark Högsbo, a new facility three times larger with a repair workshop, reuse shop and reuse collection. Because Högsbo is close by, we keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Stadsmissionen and Emmaus. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill. In Majorna, with its strong second-hand and reuse culture, that is especially fitting.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Majorna with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Majorna's landshövdingehus, narrow wooden staircases and cobbled streets, from Karl Johansgatan and Mariaplan to Stigberget and Klippan, and that Högsbo recycling centre is close by. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Used to buildings without elevators –",
                text: "Most landshövdingehus in Majorna have no elevator. We specialise in carrying heavy furniture and appliances down narrow wooden staircases – safely and without damaging the stairwell. No extra charges for stairs.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any area in Majorna, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your courtyard, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via Högsbo recycling centre and Kretslopp och vatten's facilities. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Majorna cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any area in Majorna.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Majorna?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "Can you carry heavy furniture down a landshövdingehus without an elevator in Majorna?",
          answer:
            "Yes, that is our speciality. Most landshövdingehus buildings in Majorna have no elevator and have narrow wooden staircases. We are used to carrying down sofas, appliances, beds and more safely and without damaging the stairwell – regardless of floor, and with no extra charge for stairs.",
        },
        {
          question: "How quickly can you come for junk removal in Majorna?",
          answer:
            "We have vans and staff across the Gothenburg region and know Majorna's streets well, so we get there quickly. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Majorna?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "How do you handle parking on the narrow streets of Majorna?",
          answer:
            "We are used to Majorna's narrow and sometimes cobbled streets. Tell us at booking whether there is a loading zone, courtyard or space near the entrance, and we'll plan where to put the van so the loading goes smoothly.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through narrow doors or down narrow stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Majorna insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, courtyard and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Majorna?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. For Majorna that is usually Högsbo recycling centre just south of the district, run by Kretslopp och vatten. Items that can be reused are donated to Stadsmissionen or Emmaus.",
        },
        {
          question: "Can I book junk removal in Majorna on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from a basement or attic in Majorna?",
          answer:
            "Yes, we pick up items from all spaces – basement storage, attic storage and the shared spaces common in Majorna's landshövdingehus. We carry them up and down the stairs for you.",
        },
        {
          question: "Can you make multiple stops during junk removal in Majorna?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment at Mariaplan and a storage unit at another address in Majorna, for example.",
        },
        {
          question: "Which areas in Majorna do you cover?",
          answer:
            "We perform junk removal across all of Majorna – Stigberget, Kungsladugård, Sandarna, Sanna, Kungssten, Sjöbergen, Klippan and Röda Sten, as well as the area around Karl Johansgatan and Mariaplan. No extra charges for any area.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Majorna?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Gothenburg resident you can drop those off for free at Högsbo recycling centre.",
        },
        {
          question: "Which municipalities around Majorna do you serve?",
          answer:
            "We operate across the entire Gothenburg region, including Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Majorna in 3 easy steps",
        step2Text:
          "Our young workers in Majorna come to you and collect whatever you want removed. We handle heavy lifting and carry furniture and appliances down the narrow wooden staircases of the landshövdingehus buildings – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Majorna today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across all of Majorna, even in buildings without an elevator.",
      },
    },
  },
];
